import { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/misc';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';

// DeleteAccountDialog — fluxo de EXCLUSÃO DEFINITIVA em 2 passos (aviso → OTP por
// e-mail → confirmar). `base` é o prefixo dos endpoints: '/account/delete' (própria
// conta) ou `/platform/tenants/{id}/delete` (admin da plataforma).
export function DeleteAccountDialog({ open, onOpenChange, base, onScheduled }: {
  open: boolean; onOpenChange: (o: boolean) => void; base: string; onScheduled?: () => void;
}) {
  const t = useT();
  const [step, setStep] = useState<'warn' | 'code'>('warn');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep('warn'); setCode(''); setBusy(false); };
  const close = () => { onOpenChange(false); reset(); };

  const sendCode = async () => {
    setBusy(true);
    try {
      const r = await apiFetch('POST', `${base}/request`);
      if (!r.ok) { toast.error(t('account.del.requestFailed')); return; }
      toast.success(t('account.del.codeSent'));
      setStep('code');
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    if (code.length < 6) return;
    setBusy(true);
    try {
      const r = await apiFetch('POST', `${base}/confirm`, { code });
      if (!r.ok) {
        const c = (r.data as { code?: string })?.code;
        toast.error(c === 'already_scheduled' ? t('account.del.already') : t('account.del.confirmFailed'));
        return;
      }
      toast.success(t('account.del.scheduledToast'));
      close();
      onScheduled?.();
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else onOpenChange(true); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive"><AlertTriangle className="size-5" /> {t('account.del.dialogTitle')}</DialogTitle>
          <DialogDescription>{t('account.del.warn')}</DialogDescription>
        </DialogHeader>
        {step === 'code' && (
          <div className="flex flex-col gap-2 py-1">
            <Label htmlFor="del-code">{t('account.del.codeLabel')}</Label>
            <Input
              id="del-code" inputMode="numeric" maxLength={6} autoFocus value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="text-center font-mono text-lg tracking-[0.4em]"
            />
            <p className="text-xs text-muted-foreground">{t('account.del.codeSent')}</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close}>{t('common.cancel')}</Button>
          {step === 'warn' ? (
            <Button variant="destructive" disabled={busy} onClick={sendCode}>
              {busy && <Spinner className="border-white/40 border-t-white" />} {t('account.del.sendCode')}
            </Button>
          ) : (
            <Button variant="destructive" disabled={busy || code.length < 6} onClick={confirm}>
              {busy && <Spinner className="border-white/40 border-t-white" />} {t('account.del.confirm')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
